import { OnboardingFlowEditorState } from 'src/reducers/onboardingFlowEditor/types'
import { EMPTY_VALIDATION, validateDocument } from 'src/utils/flowEditorDocument/validateDocument'
import type {
  DocumentValidationResult,
  FlowEditorTemplateDocument,
} from 'src/utils/flowEditorDocument/types'

interface RootState {
  onboardingFlowEditor: OnboardingFlowEditorState
}

export const getFlowEditorTemplate = (state: RootState) => state.onboardingFlowEditor.template

export const getFlowEditorSavedSnapshot = (state: RootState) =>
  state.onboardingFlowEditor.savedSnapshot

export const getFlowEditorDraft = (state: RootState) => state.onboardingFlowEditor.draft

export const getFlowEditorLoading = (state: RootState) => state.onboardingFlowEditor.loading

export const getFlowEditorSaving = (state: RootState) => state.onboardingFlowEditor.saving

export const getFlowEditorError = (state: RootState) => state.onboardingFlowEditor.error

/**
 * Memoizes draft validation on the draft reference (each edit produces a new
 * draft object) so consumers re-run `validateDocument` only on an actual
 * change. Held in a class instance rather than module-level `let`s.
 */
class FlowEditorDraftValidationCache {
  private cachedDraft: FlowEditorTemplateDocument | null | undefined
  private cachedValidation: DocumentValidationResult = EMPTY_VALIDATION

  public get(draft: FlowEditorTemplateDocument | null): DocumentValidationResult {
    if (draft === this.cachedDraft) {
      return this.cachedValidation
    }

    this.cachedDraft = draft
    this.cachedValidation = draft ? validateDocument(draft) : EMPTY_VALIDATION

    return this.cachedValidation
  }
}

const draftValidationCache = new FlowEditorDraftValidationCache()

/**
 * Validation of the current draft document, recomputed whenever the draft
 * changes — the pure "run validation onChange" derivation, no stored state.
 */
export const getFlowEditorDraftValidation = (state: RootState): DocumentValidationResult => {
  return draftValidationCache.get(state.onboardingFlowEditor.draft)
}
